/**
 * WeaveIn.ai Platform - Envelope Viewer
 * Shows all signatories and their signature status for a document.
 */

import React, { useState, useEffect } from 'react';
import {
  CheckCircle, Clock, User, PenTool, Download, Shield, FileText, XCircle,
  Mail, RefreshCw, Link2, Eye, Send,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { config } from '@/src/config';

interface EnvelopeSigner {
  request_id: number;
  signer_name: string;
  signer_email: string;
  signer_role: string;
  status: string;
  signed_at: string | null;
  signing_url: string | null;
  sent_at: string | null;
  sent_via: string | null;
  expires_at: string | null;
  signature: {
    id: number;
    type: string;
    data: string;
    actual_signer_name: string;
    signed_at: string;
    signer_ip: string;
    hash: string;
    consent_accepted_at: string;
  } | null;
}

interface EnvelopeData {
  title: string;
  document_file_name: string;
  total_signers: number;
  signed_count: number;
  all_signed: boolean;
  status: string;
  signers: EnvelopeSigner[];
}

interface EnvelopeViewerProps {
  requestId: number;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

export function EnvelopeViewer({ requestId }: EnvelopeViewerProps) {
  const [envelope, setEnvelope] = useState<EnvelopeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const fetchEnvelope = () => {
    fetch(`${config.weaveinApi.baseUrl}/api/signatures/envelope/${requestId}`, {
      credentials: 'include', headers: getAuthHeaders(),
    })
      .then(r => r.ok ? r.json() : null)
      .then(setEnvelope)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchEnvelope(); }, [requestId]);

  const resendToSigner = async (signerRequestId: number) => {
    setResendingId(signerRequestId);
    try {
      const res = await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests/${signerRequestId}/send`, {
        method: 'POST', credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ send_via: 'email' }),
      });
      if (!res.ok) throw new Error('Send failed');
      const data = await res.json();
      alert(data.delivered ? 'Email resent successfully' : 'Request processed but delivery unconfirmed');
    } catch (e: any) {
      alert(`Resend failed: ${e.message}`);
    } finally {
      setResendingId(null);
    }
  };

  const copySigningLink = (url: string, reqId: number) => {
    navigator.clipboard.writeText(url);
    setCopiedId(reqId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) return <div className="text-xs text-muted-foreground">Loading envelope...</div>;
  if (!envelope) return null;

  const progress = envelope.total_signers > 0
    ? (envelope.signed_count / envelope.total_signers) * 100 : 0;

  const openProof = (reqId: number) => {
    const url = `${config.weaveinApi.baseUrl}/api/signatures/requests/${reqId}/proof/html`;
    fetch(url, { headers: getAuthHeaders() })
      .then(r => r.text())
      .then(html => {
        const win = window.open('', '_blank');
        if (win) { win.document.write(html); win.document.close(); }
      });
  };

  const SIGNER_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#a855f7', '#f59e0b'];

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600" />
          <div>
            <h4 className="font-semibold text-sm">{envelope.title}</h4>
            {envelope.document_file_name && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <FileText className="w-3 h-3" /> {envelope.document_file_name}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={envelope.all_signed ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
            {envelope.all_signed ? 'Completed' : `${envelope.signed_count}/${envelope.total_signers} signed`}
          </Badge>
          {envelope.document_file_name && envelope.signed_count > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={async () => {
                try {
                  const url = `${config.weaveinApi.baseUrl}/api/signatures/envelope/${requestId}/signed-document`;
                  const token = localStorage.getItem('auth_token');
                  const cboId = localStorage.getItem('active_cbo_id');
                  const res = await fetch(url, {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'X-CBO-ID': cboId || '',
                    },
                  });
                  if (!res.ok) {
                    const errText = await res.text();
                    alert(`Download failed: ${errText}`);
                    return;
                  }
                  const contentType = res.headers.get('content-type') || '';
                  if (!contentType.includes('pdf')) {
                    const text = await res.text();
                    alert(`Expected PDF but got: ${contentType}\n${text.slice(0, 200)}`);
                    return;
                  }
                  const blob = await res.blob();
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `signed_${envelope.title.replace(/\s+/g, '_')}.pdf`;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
                } catch (e: any) {
                  alert(`Could not download: ${e.message}`);
                }
              }}
            >
              <Download className="w-3 h-3 mr-1" /> Signed Document
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <Progress value={progress} className="h-2" />

      {/* Signers */}
      <div className="space-y-2">
        {envelope.signers.map((signer, idx) => (
          <Card key={signer.request_id} className={signer.status === 'signed' ? 'border-green-200' : ''}>
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                {/* Signer avatar */}
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: SIGNER_COLORS[idx] || '#6b7280' }}
                >
                  {signer.signer_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??'}
                </div>

                {/* Signer info */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{signer.signer_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {signer.signer_email && <span>{signer.signer_email} · </span>}
                    <span className="capitalize">{signer.signer_role}</span>
                  </div>
                </div>

                {/* Status + Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {signer.status === 'signed' ? (
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <div className="text-right">
                        <div className="text-xs font-medium text-green-700">Signed</div>
                        <div className="text-[10px] text-muted-foreground">
                          {signer.signed_at ? new Date(signer.signed_at).toLocaleString() : ''}
                        </div>
                      </div>
                    </div>
                  ) : signer.status === 'viewed' ? (
                    <Badge variant="secondary" className="text-xs"><Eye className="w-3 h-3 mr-0.5" /> Viewed</Badge>
                  ) : signer.status === 'sent' ? (
                    <Badge variant="secondary" className="text-xs"><Send className="w-3 h-3 mr-0.5" /> Sent</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs"><Clock className="w-3 h-3 mr-0.5" /> Pending</Badge>
                  )}

                  {/* Per-signer actions for unsigned requests */}
                  {signer.status !== 'signed' && signer.signer_email && (
                    <Button size="sm" variant="ghost" className="h-6 px-1.5"
                      disabled={resendingId === signer.request_id}
                      onClick={() => resendToSigner(signer.request_id)}
                      title={`Resend email to ${signer.signer_email}`}>
                      {resendingId === signer.request_id
                        ? <RefreshCw className="w-3 h-3 animate-spin" />
                        : <Mail className="w-3 h-3" />}
                    </Button>
                  )}
                  {signer.status !== 'signed' && signer.signing_url && (
                    <Button size="sm" variant="ghost" className="h-6 px-1.5"
                      onClick={() => copySigningLink(signer.signing_url!, signer.request_id)}
                      title="Copy signing link">
                      {copiedId === signer.request_id
                        ? <CheckCircle className="w-3 h-3 text-green-600" />
                        : <Link2 className="w-3 h-3" />}
                    </Button>
                  )}
                </div>
              </div>

              {/* Timeline details for unsigned signers */}
              {signer.status !== 'signed' && (signer.sent_at || signer.expires_at) && (
                <div className="mt-1.5 text-[10px] text-muted-foreground flex items-center gap-3">
                  {signer.sent_at && <span>Sent {new Date(signer.sent_at).toLocaleString()}{signer.sent_via ? ` via ${signer.sent_via}` : ''}</span>}
                  {signer.expires_at && <span>Expires {new Date(signer.expires_at).toLocaleDateString()}</span>}
                </div>
              )}

              {/* Signature preview */}
              {signer.signature && signer.signature.data?.startsWith('data:image') && (
                <div className="mt-2 flex items-center gap-3 p-2 bg-green-50 rounded-md border border-green-100">
                  <img src={signer.signature.data} alt="Signature" className="h-10 opacity-80" />
                  <div className="text-xs text-green-700">
                    <div>Signed by {signer.signature.actual_signer_name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {signer.signature.type} · IP: {signer.signature.signer_ip || 'N/A'} · Hash: {signer.signature.hash?.slice(0, 12)}...
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="ml-auto h-6 px-2 text-xs" onClick={() => openProof(signer.request_id)}>
                    <Download className="w-3 h-3 mr-0.5" /> Proof
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
